import React from 'react';
import { Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import type { ChangeRequest } from '../../../types/changes';
import { isEmergencyChangeType, labelChangeStatus, labelChangeType } from '../changeLabels';
import { formatChangePublicId } from '../changePublicId';
import { formatGoLiveWindow, formatGovernanceDate, formatGovernanceWindow } from '../goLiveWindow';
import { nativeDateRevisionOf, dateRevisionOf } from '../changeDateRevisions';
import {
  detailSummaryRowLabel,
  detailSummaryUsesFullWidth,
  formatDetailSummaryValue,
  type DetailSummaryLayoutField,
  visibleDetailSummaryFields,
} from '../changeDetailSummaryDisplay';
import ChangeDateRevisionHistory from './ChangeDateRevisionHistory';

interface Props {
  change: ChangeRequest;
  summaryLayout?: DetailSummaryLayoutField[];
  showPostImplementation?: boolean;
  /** Link para editor da apresentação CAB (somente gestor). */
  cabDeckHref?: string;
}

const ChangeHeader: React.FC<Props> = ({
  change,
  summaryLayout = [],
  showPostImplementation = false,
  cabDeckHref,
}) => {
  const navigate = useNavigate();
  const emergency = isEmergencyChangeType(change.change_type);
  const useConfiguredLayout = summaryLayout.length > 0;
  const rows = useConfiguredLayout ? visibleDetailSummaryFields(summaryLayout, change) : null;

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-3">
          <div>
            <h4 className="mb-1">
              {formatChangePublicId(change.id)} — {change.title}
            </h4>
            <div className="text-muted">
              Tipo: {labelChangeType(change.change_type)} | Categoria:{' '}
              {change.category?.name || 'Não informada'} | Projeto: {change.project}
            </div>
          </div>
          <div className="d-flex flex-column align-items-end gap-2">
            <Badge
              bg={
                change.status === 'encerrar'
                  ? 'success'
                  : change.status === 'cancelada'
                    ? 'danger'
                    : 'secondary'
              }
            >
              {change.status === 'encerrar' ? 'Encerrada' : labelChangeStatus(change.status)}
            </Badge>
            {cabDeckHref ? (
              <Button
                type="button"
                size="sm"
                variant="outline-primary"
                onClick={() => navigate(cabDeckHref)}
              >
                Apresentação CAB
              </Button>
            ) : null}
          </div>
        </div>
        <Row className="g-3">
          {rows
            ? rows.map((field) => {
                const full = detailSummaryUsesFullWidth(field);
                return (
                  <Col key={field.id} md={full ? 12 : 6} xs={12}>
                    <strong>{detailSummaryRowLabel(field, change)}</strong>{' '}
                    {full ? (
                      <div>
                        {formatDetailSummaryValue(field, change)}
                        <ChangeDateRevisionHistory
                          revision={dateRevisionOf(change.date_revisions, field)}
                        />
                      </div>
                    ) : (
                      <>
                        {formatDetailSummaryValue(field, change)}
                        <ChangeDateRevisionHistory
                          revision={dateRevisionOf(change.date_revisions, field)}
                        />
                      </>
                    )}
                  </Col>
                );
              })
            : null}
          {!useConfiguredLayout ? (
            <>
              <Col md={6}>
                <strong>Dono da Mudança:</strong> {change.owner?.name || 'Não informado'}
              </Col>
              <Col md={6}>
                <strong>Solicitante:</strong> {change.requester?.name || 'Não informado'}
              </Col>
              <Col md={6}>
                <strong>Área solicitante:</strong> {change.requester_area?.name || 'Não informada'}
              </Col>
              <Col md={6}>
                <strong>{emergency ? 'Início e fim da implementação:' : 'Início e fim do Go Live:'}</strong>{' '}
                {formatGoLiveWindow(change.go_live_start_at, change.go_live_end_at)}
                <ChangeDateRevisionHistory
                  revision={nativeDateRevisionOf(change.date_revisions, 'go_live_window')}
                />
              </Col>
              <Col md={6}>
                <strong>Usuários impactados:</strong> {change.impacted_users}
              </Col>
              <Col md={6}>
                <strong>Agendamento Riscos e Impactos:</strong>{' '}
                {formatGovernanceWindow(change.risk_committee_start_at, change.risk_committee_end_at)}
                <ChangeDateRevisionHistory
                  revision={nativeDateRevisionOf(change.date_revisions, 'risk_committee_window')}
                />
              </Col>
              <Col md={6}>
                <strong>Agendamento CAMUD Go/No-Go:</strong>{' '}
                {formatGovernanceDate(change.camud_go_no_go_date)}
                <ChangeDateRevisionHistory
                  revision={nativeDateRevisionOf(change.date_revisions, 'camud_go_no_go_date')}
                />
              </Col>
              <Col md={12}>
                <strong>Descrição:</strong>
                <div>{change.description}</div>
              </Col>
              {!change.scope ? null : (
                <Col md={12}>
                  <strong>Escopo:</strong>
                  <div>{change.scope}</div>
                </Col>
              )}
              <Col md={12}>
                <strong>Plano de Rollback:</strong>
                <div>{change.rollback_plan}</div>
              </Col>
            </>
          ) : null}
          {showPostImplementation ? (
            <Col md={6}>
              <strong>Rollback Executado:</strong> {change.rollback_executed ? 'Sim' : 'Não'}
            </Col>
          ) : null}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default ChangeHeader;
